<template>
  <card class="pa-4">
    <v-row no-gutters class="mb-4">
      <v-col>
        <language-select :langs="nodeLangs" @change-lang="emitFetch" />
      </v-col>
      <v-spacer />
      <v-col v-if="nodeClass.can_publish == 1" class="flex-grow-0">
        <v-switch
          v-model="published[nodeLang]"
          inset
          :false-value="0"
          :true-value="1"
          class="pl-4 pt-0 my-0"
          :readonly="!$canEdit()"
          label="Опубликовано"
          hide-details
          @change="toggleThisPublished"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <data-table
        ref="table-hoc"
        :height-offset="292"
        :filters.sync="filters"
        :schema="filterSchema"
        :headers="headers"
        :count="count"
        :loading="loading"
        :items="items"
        :table-options="dataTableProps"
        @fetch="loadChildren"
      >
        <template v-slot:before-filter>
          <v-btn
            color="primary"
            depressed
            outlined
            class="flex-grow-0 mr-4 sv-btn-icon"
            @click="editNode(nodeId)"
          >
            <v-icon>mdi-cog-outline</v-icon>
          </v-btn>
          <div v-if="$canAdd() && childrenClasses.length <= 1">
            <v-btn
              v-for="(childClass, index) in childrenClasses"
              :key="index"
              color="primary"
              depressed
              class="mr-4"
              @click="addNode(childClass.id)"
            >
              <v-icon>mdi-plus</v-icon>
              {{ childClass.title }}
            </v-btn>
          </div>
          <div v-else-if="$canAdd()">
            <v-menu :rounded="false" offset-y class="mr-4 flex-grow-0">
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  color="primary"
                  depressed
                  class="mr-4"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                  Элемент
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  v-for="childrenClass in childrenClasses"
                  :key="childrenClass.id"
                  link
                  @click="addNode(childrenClass.id)"
                >
                  <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
                  <v-list-item-title v-text="childrenClass.title" />
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>

        <template #item="{ item }">
          <tr :class="mirrorId(item) ? 'mirrored' : ''">
            <td v-if="!hasDateTags">
              {{ item.seq }}
            </td>
            <td>
              <div class="d-flex flex-column align-center">
                <span>{{ item.id }}</span>
                <v-icon v-if="mirrorId(item)">mdi-arrow-up</v-icon>
                <span v-if="mirrorId(item)" class="text-disabled--text">{{
                  mirrorId(item)
                }}</span>
              </div>
            </td>
            <td v-if="publishableChildren">
              <v-switch
                v-if="canPublish(item.class_id)"
                v-model="item.published[nodeLang]"
                inset
                dense
                :false-value="0"
                :true-value="1"
                class="pl-4 pt-0 my-2"
                :readonly="!$canEdit()"
                hide-details
                @change="toggleChildPublished(item)"
              />
            </td>
            <td v-if="hasManyChildTyps">
              {{ item.class_description }}
            </td>
            <td v-for="header in tagHeaders" :key="header.value">
              <router-link
                v-if="header.value == 'title'"
                :to="
                  mirrorId(item)
                    ? {
                        name: 'NodeWithParentSpec',
                        params: { id: mirrorId(item), pid: nodeId }
                      }
                    : { name: 'Node', params: { id: item.id } }
                "
                class="sv-toggler"
                >{{ cellValue(item, header) }}</router-link
              >
              <div v-else-if="header.typ == 'TAGS'">
                <badge
                  v-for="(tag, tagIndex) in cellValue(item, header)"
                  :key="tagIndex"
                  :text="tag"
                />
              </div>
              <div
                v-else-if="header.typ == 'CHECKBOX'"
                class="d-flex justify-center"
              >
                <v-checkbox
                  v-if="cellValue(item, header) == '1'"
                  readonly
                  :input-value="true"
                />
                <v-checkbox v-else readonly :input-value="false" />
              </div>
              <div v-else-if="header.typ == 'SCHEDULE'">
                <schedule-dates-interval :schedule="cellValue(item, header)" />
              </div>
              <div v-else-if="header.typ == 'IMAGE'" class="pa-2">
                <v-img
                  v-if="cellValue(item, header)"
                  :src="cellValue(item, header)"
                  height="120"
                  width="210"
                />
              </div>
              <div
                v-else-if="
                  ['IMAGE_EXT', 'IMAGE_MINIATURE'].includes(header.typ)
                "
                class="pa-2"
              >
                <v-img
                  v-if="cellValue(item, header)"
                  :src="cellValue(item, header).i_main"
                  height="120"
                  width="210"
                />
              </div>
              <div v-else-if="header.typ == 'FILE'" class="pa-2">
                <a
                  v-if="cellValue(item, header)"
                  :href="cellValue(item, header)"
                  class="sv-toggler"
                >
                  {{ getBaseName(cellValue(item, header)) }}
                </a>
              </div>
              <div
                v-else
                :class="header.value == 'phones' ? 'text-no-wrap' : ''"
                v-html="nl2br(cellValue(item, header))"
              />
            </td>
            <td v-for="header in linkHeaders" :key="header.value">
              <div
                v-for="(title, index) in getRefTitlesByGroup(
                  item,
                  header.value
                )"
                :key="index"
              >
                {{ title }}
              </div>
            </td>
            <td>
              <action-col>
                <action-col-btn
                  v-if="$canViewHistory()"
                  :id="`btn-view-history-${item.id}`"
                  tooltip="История изменений"
                  @click="showHistory(item.id)"
                >
                  mdi-history
                </action-col-btn>
                <action-col-btn
                  v-if="wwwPath(item) !== ''"
                  :id="`btn-www-${item.id}`"
                  tooltip="Просмотр на сайте"
                  :href="wwwPath(item)"
                  target="checking-www"
                >
                  mdi-monitor
                </action-col-btn>
                <action-col-btn
                  v-if="$canEdit() && mirrorId(item)"
                  :id="`btn-edit-order-${item.id}`"
                  tooltip="Изменение порядка и публикации"
                  @click="editOrder(item.id)"
                >
                  mdi-order-numeric-ascending
                </action-col-btn>
                <action-col-btn
                  v-if="mirrorId(item)"
                  :id="`btn-edit-mirror-${item.id}`"
                  :tooltip="
                    $canEdit() ? 'Изменить источник' : 'Просмотр источника'
                  "
                  :to="{
                    name: 'NodeWithParentSpec',
                    params: { id: mirrorId(item), pid: nodeId }
                  }"
                >
                  mdi-pencil-box-multiple-outline
                </action-col-btn>
                <action-col-btn
                  v-else
                  :id="`btn-edit-${item.id}`"
                  :tooltip="$canEdit() ? 'Изменить' : 'Просмотр'"
                  :to="{ name: 'Node', params: { id: item.id } }"
                >
                  {{
                    $canEdit()
                      ? "mdi-square-edit-outline"
                      : "mdi-arrow-right-bold-box-outline"
                  }}
                </action-col-btn>
                <action-col-btn
                  v-if="childCanHaveChildren(item) && !mirrorId(item)"
                  :id="`btn-go-${item.id}`"
                  :tooltip="$canEdit() ? 'Содержимое' : 'Просмотр содержимого'"
                  :to="{ name: 'NodeChildren', params: { id: item.id } }"
                >
                  mdi-file-tree
                </action-col-btn>
                <action-col-btn
                  v-if="$canDelete() && deletable(item.id) && !mirrorId(item)"
                  :id="`btn-delete-${item.id}`"
                  tooltip="Удалить"
                  @click="deleteNode(item.id)"
                >
                  mdi-close
                </action-col-btn>
                <action-col-btn
                  v-else-if="
                    $canDelete() && deletable(item.id) && mirrorId(item)
                  "
                  :id="`btn-delete-mirror-${item.id}`"
                  tooltip="Удалить отражение"
                  @click="deleteNode(item.id)"
                >
                  mdi-close-box-multiple-outline
                </action-col-btn>
              </action-col>
            </td>
          </tr>
        </template>
      </data-table>
    </v-row>
    <node-dialog
      v-model="nodeDialog"
      :meta="meta"
      :node-id="selectedNodeId"
      :readonly="!$canEdit()"
      :can-sort="canSort"
      @close="nodeDialog = false"
      @apply="emitFetch"
    />
    <order-dialog
      v-model="orderDialog"
      :meta="meta"
      :node-id="selectedNodeId"
      @close="selectedNodeId = null"
      @apply="emitFetch"
    />
    <history-object-modal
      v-if="$canViewHistory()"
      v-model="historyDialog"
      :item-object-id="selectedNodeId"
      scope-name="Node"
    />
  </card>
</template>
<script>
import CheckPermissionMixin from "@/mixins/CheckPermissionMixin";
import NotificationMixin from "@/mixins/NotificationMixin";
import DialogMixin from "@/mixins/DialogMixin";
import ResizeMixin from "@/mixins/ResizeMixin";
import QueryMixin from "@/mixins/QueryMixin";

import SearchFilter from "@/components/Filters/SearchFilter";
import { isObjectTag, isArrayTag } from "@/utils/EnvHelper.js";
import SelectFilter from "@/components/Filters/SelectFilter";
import LanguageSelect from "@/components/LanguageSelect";
import DataTable from "@/components/DataTable.vue";
import Badge from "@/components/Badge.vue";

import NodeDialog from "./NodeDialog.vue";
import OrderDialog from "./OrderDialog.vue";
import HistoryObjectModal from "@/components/HistoryObjectModal.vue";

import Repository from "@/api/node";
import ScheduleDatesInterval from "@/components/ScheduleDatesInterval.vue";

export default {
  components: {
    NodeDialog,
    OrderDialog,
    DataTable,
    HistoryObjectModal,
    Badge,
    LanguageSelect,
    ScheduleDatesInterval
  },
  mixins: [
    CheckPermissionMixin,
    NotificationMixin,
    DialogMixin,
    QueryMixin,
    ResizeMixin
  ],
  props: {
    nodeId: {
      type: [String, Number],
      required: true
    },
    meta: {
      type: Object,
      required: true
    },
    canSort: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    selectedClassId: null,
    selectedNodeId: null,
    count: 0,
    options: {},
    loading: false,
    nodeDialog: false,
    filterDialog: false,
    historyDialog: false,
    orderDialog: false,
    filters: {},
    published: {},
    items: [],
    refsDic: {},
    initialQueryParams: {
      sort: [],
      page: 1,
      limit: 50
    }
  }),
  computed: {
    cellValue() {
      return (item, header) => {
        const value = item?.fields?.[header.value] || this.defaultValue(header);

        if ((value + "").match(/^#[0-9A-Fa-f]{6}$/)) {
          return (
            '<div style="background-color:' +
            value +
            ';height:40px">&nbsp;</div><div style="text-align:center">' +
            value +
            "</div>"
          );
        }
        if (header.dicId && header.typ == "STRING") {
          return this.$store.getters.getDicTranslation(
            header.dicId,
            this.nodeLang,
            value
          );
        } else {
          return value;
        }
      };
    },
    defaultValue() {
      return header => {
        switch (header.typ) {
          case "CHECKBOX":
            return "0";
          case "TAGS":
            return [];
          case "SCHEDULE":
            return {};
          case "IMAGE_EXT":
            return null;
          default:
            return "";
        }
      };
    },
    nodeData() {
      return this.$store.getters.getCurrentNode(this.nodeId);
    },
    nodeClass() {
      return this.$store.getters.getMetaClass(this.nodeData.cid);
    },
    canPublish() {
      return id => {
        const nodeClass = this.$store.getters.getMetaClass(id);
        return nodeClass?.can_publish == 1;
      };
    },
    mirrorId() {
      return item => item.mirror_node_id;
    },
    nodeLangs() {
      return this.$store.getters.getLangs;
    },
    nodeLang() {
      return this.$store.getters.getLang;
    },
    deletable() {
      return nodeId => {
        const nodeData = this.$store.getters.getNodeFromAnyCache(nodeId);
        if (!nodeData) return false;
        return this.meta.children.some(
          link => link.class_id_2 == nodeData.cid && link.can_add_delete == 1
        );
      };
    },
    nodeTitle() {
      return nodeId => {
        const nodeData = this.$store.getters.getNodeFromAnyCache(nodeId);
        return nodeData?.t?.[this.nodeLang] || "";
      };
    },
    childrenClassesAll() {
      return this.$store.getters.getMetaChildren(this.nodeData.cid);
    },
    childrenClassesAllIndex() {
      return this.childrenClassesAll.reduce((index, entry) => {
        index[entry.class_id_2] = entry;
        return index;
      }, {});
    },
    publishableChildren() {
      const local = this;
      return this.childrenClassesAll.some(child =>
        local.canPublish(child.class_id_2)
      );
    },
    childrenClasses() {
      return this.childrenClassesAll
        .filter(child => child.can_add_delete == 1)
        .map(child => ({
          title: this.$store.getters.getMetaClass(child.class_id_2).description,
          id: child.class_id_2
        }));
    },
    hasManyChildTyps() {
      return this.childrenClassesAll.length > 1;
    },
    childCanHaveChildren() {
      return childItem => {
        const grandChildren =
          this.$store.getters.getMetaChildren(childItem.class_id) || [];
        return grandChildren.length > 0;
      };
    },
    wwwPath() {
      return childItem => {
        const classMeta = this.$store.getters.getMetaClass(childItem.class_id);
        const linkData = this.childrenClassesAllIndex[childItem.class_id];
        const L = this.nodeData.p.length;
        const www_path = this.nodeData.p.reduce((path, entry, i) => {
          const ind = L - i;
          return path.replace(`{node_name:p${ind}}`, entry.pn);
        }, linkData?.custom_settings?.www_path || classMeta?.custom_settings?.www_path || "");
        return www_path
          .replace("{node_name}", childItem.node_name)
          .replace("{id}", childItem.id);
      };
    },
    // Посмотрим какие поля есть общие у всех дочерних элементов (как минимум title там будет)
    // UPD: теперь берем не пересечение, а объединение полей. Главное чтобы у них стояло show_in_table
    tagsAllowed() {
      const rawList = this.meta.children.reduce((product, link) => {
        const tagNames = this.$store.getters
          .getMetaTags(link.class_id_2)
          .filter(
            tag =>
              !["CODE", "TEXT", "IMAGE_GALLERY"].includes(tag.typ) &&
              tag.show_in_table == 1
          )
          .map(tag => tag.name);
        if (product === null) {
          return tagNames;
        } else {
          return [...product, ...tagNames];
        }
      }, null);
      return [...new Set(rawList)];
    },

    // Заголовки полей
    tagHeaders() {
      return this.$store.getters
        .getMetaTags(this.meta.children[0].class_id_2)
        .map(tag => ({
          text: tag.description,
          value: tag.name,
          typ: tag.typ,
          dicId: tag.realm_dic_id,
          sortable: !isObjectTag(tag.typ) && !isArrayTag(tag.typ)
        }))
        .filter(header => this.tagsAllowed.includes(header.value));
    },

    // Посмотрим какие группы ссылок есть у всех элементов
    linksAllowed() {
      const local = this;
      return Object.entries(
        this.meta.children.reduce((product, link) => {
          const refs =
            this.$store.getters.getMetaReferences(link.class_id_2) || {};
          return Object.values(refs).reduce((index, refArray) => {
            refArray.forEach(ref => {
              if (
                !this.thisNodeHasParentClass(
                  ref.custom_settings?.filter_parent_class || ""
                )
              ) {
                return;
              }
              const gr = local.$store.getters.getMetaLinkGroup(
                ref.realm_class_link_group_id
              );
              if (
                gr.show_in_table === 1 &&
                index[ref.class_id_2] === undefined
              ) {
                index[ref.class_id_2] = { show: 1, gr };
              } else if (gr.show_in_table === 0) {
                index[ref.class_id_2] = { show: 0, gr };
              }
            });
            return index;
          }, product);
        }, {})
      )
        .filter(entry => entry[1].show === 1)
        .map(entry => ({ class_id_2: entry[0], group: entry[1].gr }));
    },

    // Заголовки ссылок
    linkHeaders() {
      return this.linksAllowed.map(entry => ({
        text: entry.group.description,
        value: entry.class_id_2,
        sortable: false
      }));
    },

    // Получить названия ссылок
    getRefTitlesByGroup() {
      return (item, classId2) => {
        const className = this.$store.getters.getMetaClass(classId2).name;
        return (item?.links?.[className] || []).map(nodeId =>
          this.nodeTitle(nodeId)
        );
      };
    },

    hasDateTags() {
      return this.tagHeaders.some(item => item.typ == "DATE");
    },

    sortField() {
      if (!this.hasDateTags) return "seq";
      const foundItem = this.tagHeaders.find(item => item.typ == "DATE");
      return foundItem.value;
    },

    dataTableProps() {
      // по умолчанию
      const props = {
        sortBy: ["seq"],
        sortDesc: [false],
        page: this.initialQueryParams.page,
        itemsPerPage: this.initialQueryParams.limit
      };

      // если специально указано по какому полю и как упорядочивать дочерние элементы
      if (this.initialQueryParams.sort.length > 0) {
        props.sortBy = [];
        props.sortDesc = [];
        this.initialQueryParams.sort.forEach(item => {
          props.sortBy.push(item[0]);
          props.sortDesc.push(item[1] == "desc");
        });
      } else if (this.nodeClass.custom_settings?.sort_children) {
        const assets = this.nodeClass.custom_settings.sort_children.split(" ");
        const sortDesc = assets[1] && assets[1] == "desc";
        props.sortBy = [assets[0]];
        props.sortDesc = [sortDesc];

        // если в тэгах есть поле с типом DATE, упорядочим от последнего к первому
      } else if (this.hasDateTags) {
        const foundItem = this.tagHeaders.find(item => item.typ == "DATE");
        props.sortBy = [foundItem.value];
        props.sortDesc = [true];
      }

      return props;
    },

    // Все заголовки таблицы дочерних элементов
    headers() {
      const orderHeader = this.hasDateTags
        ? []
        : [{ text: "Порядок", value: "seq", width: "40px" }];
      const typHeader = this.hasManyChildTyps
        ? [{ text: "Тип", value: "class_name" }]
        : [];
      const publishedHeader = this.publishableChildren
        ? [{ text: "Опубликовано", value: "published", width: "80px" }]
        : [];
      const prependHeaders = [{ text: "ID", value: "id", width: "40px" }];
      const appendHeaders = [
        {
          text: "Действия",
          value: "actions",
          sortable: false,
          align: "center",
          width: "155px"
        }
      ];
      return [
        ...orderHeader,
        ...prependHeaders,
        ...publishedHeader,
        ...typHeader,
        ...this.tagHeaders,
        ...this.linkHeaders,
        ...appendHeaders
      ];
    },

    filterSchema() {
      const lang = this.$store.getters.getLang;
      return {
        commonGroup: {
          filters: {
            id: {
              component: SearchFilter,
              title: "ID",
              filterBind: { showMethod: false, defaultLike: false },
              like: false
            },
            published: {
              component: SelectFilter,
              title: "Опубликовано",
              filterBind: {
                items: [
                  { id: 1, title: "Да" },
                  { id: 0, title: "Нет" }
                ],
                valueItem: "id",
                textItem: "title"
              }
            },
            ...this.tagHeaders.reduce((product, item) => {
              if (item.typ == "STRING" && item.dicId) {
                product[item.value] = {
                  component: SelectFilter,
                  title: item.text,
                  filterBind: {
                    items: this.$store.getters.getDic(item.dicId, lang),
                    valueItem: "term",
                    textItem: "value"
                  }
                };
              } else if (["STRING", "MEMO"].includes(item.typ)) {
                product[item.value] = {
                  component: SearchFilter,
                  title: item.text,
                  filterBind: { showMethod: false, defaultLike: true },
                  like: true
                };
              } else if (item.typ == "TAGS" && item.dicId) {
                product[item.value] = {
                  component: SelectFilter,
                  title: item.text,
                  filterBind: {
                    items: this.$store.getters.getDic(item.dicId, lang),
                    valueItem: "value",
                    textItem: "term"
                  }
                };
              } else if (item.typ == "SCHEDULE") {
                product[item.value] = {
                  component: SelectFilter,
                  title: item.text,
                  filterBind: {
                    items: [
                      { id: 1, title: "Текущие" },
                      { id: 2, title: "Будущие" },
                      { id: 3, title: "Архив" },
                      { id: 4, title: "Неопределенные" }
                    ],
                    valueItem: "id",
                    textItem: "title"
                  }
                };
              }
              return product;
            }, {}),
            ...this.linkHeaders.reduce((product, item) => {
              const filterClass = this.$store.getters.getMetaClass(item.value);
              product[`aunfl_${filterClass.name}`] = {
                component: SelectFilter,
                title: item.text,
                filterBind: {
                  items: Object.values(this.refsDic[item.value] || {}).map(
                    item => ({
                      value: item.node_id_2,
                      term: item.titles?.[lang]
                    })
                  ),
                  valueItem: "value",
                  textItem: "term"
                }
              };
              return product;
            }, {})
          }
        }
      };
    },
    storageFiltersKey() {
      return `_NODE_${this.nodeId}_QUERY`;
    },
    thisNodeHasParentClass() {
      return parentClass => {
        if (parentClass.length > 0) {
          return this.nodeData.p.some(item => {
            const classMeta = this.$store.getters.getMetaClass(item.c);
            return classMeta.name === parentClass;
          });
        } else {
          return true;
        }
      };
    }
  },
  mounted() {
    this.loadQueryParams();
  },
  methods: {
    getBaseName(path) {
      if (!path) return "";
      const slashIndex = path.lastIndexOf("/");
      return path.substr(slashIndex + 1);
    },
    async loadChildren(params) {
      try {
        this.loading = true;
        const { data } = await Repository.getChildren(this.nodeId, params);
        this.items = data.items;
        this.refsDic = data.refsDic;
        this.count = data.count;
        this.published = data.published;
        this.$store.commit("setChildrenNodesCache", data.childrenCache);
        this.$store.commit("setParentNodesCache", data.parentCache);
        this.saveQueryParams(params);
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },
    loadQueryParams() {
      const paramsStr = localStorage.getItem(this.storageFiltersKey);
      if (!paramsStr) return;
      const queryParams = JSON.parse(paramsStr);
      this.filters = { ...this.filters, ...queryParams.filters };
      if (queryParams.limit) this.initialQueryParams.limit = queryParams.limit;
      if (queryParams.page) this.initialQueryParams.page = queryParams.page;
      if (queryParams.sort) this.initialQueryParams.sort = queryParams.sort;
    },
    saveQueryParams(params) {
      localStorage.setItem(this.storageFiltersKey, JSON.stringify(params));
    },
    addNode(classAdd) {
      const id = this.nodeId;
      this.$router.push({ name: "NodeAdd", params: { id, classAdd } });
    },
    editNode(id) {
      this.selectedNodeId = id;
      this.nodeDialog = true;
    },
    goNode(id, forceEdit) {
      this.$router.push({ name: "Node", params: { id, forceEdit } });
    },
    showHistory(id) {
      this.selectedNodeId = id;
      this.historyDialog = true;
    },
    editOrder(id) {
      this.selectedNodeId = id;
      this.orderDialog = true;
    },
    async deleteNode(id) {
      const confirm = await this.confirm(
        "Удаление элемента",
        "Вы действительно хотите удалить этот элемент со всем содержимым? Это действие нельзя отменить!",
        {
          width: 550
        }
      );
      if (!confirm) return;
      try {
        const { data } = await Repository.delete(id);
        this.$store.dispatch("fetchProfile");
        this.emitFetch();
        this.sendAlert(data.alert);
      } catch (error) {
        return true;
      }
    },
    emitFetch() {
      this.$refs["table-hoc"].fetchData();
    },
    async toggleChildPublished(item) {
      const published = item.published[this.nodeLang];
      const { data } = await Repository.setPublished(
        item.id,
        published,
        this.nodeLang
      );
      this.sendAlert(data.alert);
    },
    async toggleThisPublished() {
      const published = this.published[this.nodeLang];
      const { data } = await Repository.setPublished(
        this.nodeId,
        published,
        this.nodeLang
      );
      this.sendAlert(data.alert);
    },
    nl2br(str) {
      if (typeof str !== "string") return "";
      return str?.replace(/(?:\r\n|\r|\n)/g, "<br>") || "";
    }
  }
};
</script>
<style lang="scss" scoped>
@import "../../styles/branding.scss";
.sv-disabled {
  color: $brand-control-icon;
}
.sv-enabled {
  color: $brand-status-success;
}
.sv-danger {
  color: $brand-status-danger;
}
.sv-toggler {
  text-decoration: none !important;
  display: inline;
  line-height: 110%;
  color: #2196f3;
  border-bottom: 1px dashed #2196f3;
  cursor: pointer;
}
::v-deep .mirrored {
  background: repeating-linear-gradient(
    45deg,
    transparent,
    rgb(226, 239, 255, 0.5) 20px
  ) !important;
}
</style>
